import LogInterceptor from './LogInterceptor';
import ConsoleLog from './utils/ConsoleLog';

const TAG = 'WebLogRequester';

const ERROR_TYPES = {
  INVALID_PAYLOAD: 'INVALID_PAYLOAD',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
};

/**
 * This class provides the function to transfer log data to Log Gateway.
 */
export default class WebLogRequester {
  private readonly singleSubmitEndpoint: string;
  private readonly disableFetchMethod: boolean;
  private logInterceptor: LogInterceptor<object>;

  constructor({ singleSubmitEndpoint, disableFetchMethod }: EndpointInfo, logInterceptor?: LogInterceptor<object>) {
    this.logInterceptor = logInterceptor || new LogInterceptor<object>();
    this.singleSubmitEndpoint = singleSubmitEndpoint;
    this.disableFetchMethod = disableFetchMethod;
  }

  /**
   * This function provides the function to transfer log data to Log Gateway.
   *
   * @param data
   * @returns {Promise}
   */
  async send(data: object): Promise<AsyncXMLHttpResolve | void> {
    const params = this.logInterceptor.runRequestInterceptor(data);
    try {
      if (typeof fetch === 'function' && !this.disableFetchMethod) {
        return await this.fetchRequest(params);
      }

      return await this.sendXMLHttpRequest(params);
    } catch (err) {
      ConsoleLog.e(TAG, err.message);
      // throw err;
    }
  }

  /**
   * This function provides the function to transfer log data to Log Gateway.
   *
   * @param data
   * @returns {Promise}
   */
  sendSync(data: object): void {
    const img = new Image();
    const query = `record=${encodeURIComponent(JSON.stringify(data))}`;
    img.src = `${this.singleSubmitEndpoint}?${query}`;
    ConsoleLog.i(TAG, `#sendLoggingRequest - sendSync : success`);
  }

  /**
   * It is judged whether it is a valid response.
   *
   * @param request {XMLHttpRequest}
   * @returns {boolean}
   */
  private isValidResponse(request: XMLHttpRequest) {
    const responseData = request.responseText ? JSON.parse(request.responseText) : {};
    const type = responseData.type;
    return !(type && (type === ERROR_TYPES.INTERNAL_ERROR || type === ERROR_TYPES.INVALID_PAYLOAD));
  }

  /**
   * Request using Fetch API.
   *
   * @param data
   * @returns {XMLHttpRequest}Promise
   */
  private async fetchRequest(data: object): Promise<void> {
    const jsonText = this.convertJsonToString(data);
    const requestHeader = new Headers({
      'Content-Type': 'text/plain'
    });

    const fetchOptions: RequestInit = {
      method: 'POST',
      body: jsonText,
      headers: requestHeader,
      keepalive: true,
      credentials:'include'
    };


    const response = await fetch(this.singleSubmitEndpoint, fetchOptions);

    if (response.status !== 200) {
      throw new Error(`#fetch response error - send data \n${response.status} - ${response.statusText}`);
    }

    try {
      const body = await response.json();
      ConsoleLog.i(TAG, `#fetch succeeded - ${body.responseText}`);
    } catch (e) {
      ConsoleLog.e(TAG, `#fetch error - ${e.message}`);
    }
  }

  /**
   * Request using XMLHttpRequest. (method is POST)
   *
   * @param data
   * @returns {XMLHttpRequest}
   */
  private sendXMLHttpRequest(data: object): Promise<AsyncXMLHttpResolve> {
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      const jsonText = this.convertJsonToString(data);
      let isSuccess = false;

      request.open('POST', this.singleSubmitEndpoint, true);
      request.setRequestHeader('Content-Type', 'text/plain');
    
        request.withCredentials = true;
      
      request.onload = () => {
        const {
          readyState,
          status,
          statusText,
          responseText
        } = request;

        isSuccess = readyState === XMLHttpRequest.DONE && request.status === 200 && this.isValidResponse(request);

        if (isSuccess) {
          ConsoleLog.i(TAG, `#sendXMLHttpRequest - onload\n status: ${status}, statusText: ${statusText}, responseText: ${responseText}`);
          resolve({
            status,
            statusText,
            responseText
          });
        } else {
          const errorMsg = `#sendXMLHttpRequest - onload\n status: ${status}, statusText: ${statusText}, responseText: ${responseText}`;
          reject(new Error(errorMsg));
        }
      };
      request.onerror = () => {
        const {
          status,
          statusText,
          responseText
        } = request;
        const errorMsg = `#sendXMLHttpRequest - onerror\n status:${status}, statusText: ${statusText}, responseText: ${responseText}`;
        reject(new Error(errorMsg));
      };
      request.send(jsonText);
      ConsoleLog.d(TAG, `#sendXMLHttpRequest - send data \n${jsonText}`);
    });
  }

  /**
   * Convert JSON to string.
   *
   * @param data
   * @returns {string}
   */
  private convertJsonToString(data: object): string {
    return JSON.stringify(data || {});
  }
}
